import React, { useState, useEffect } from 'react';
import {
    Box, TextField, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Typography, Grid, Stack, Chip,
    LinearProgress, ToggleButton, ToggleButtonGroup, InputAdornment
} from '@mui/material';
import {
    TrendingUp, TrendingDown, AccountBalanceWallet,
    EventNote, SearchOff, Search
} from '@mui/icons-material';
import {
    collection, getDocs, getDoc, query, where, orderBy, doc
} from "firebase/firestore";
import { db, auth } from "../../firebase";

const PRIMARY = "#0d9488";
const SECONDARY = "#0f172a";

function Allwallet() {
    const [allTransactions, setAllTransactions] = useState([]);
    const [displayedTxns, setDisplayedTxns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [schoolId, setSchoolId] = useState(null);

    // Filter States
    const [viewFilter, setViewFilter] = useState('weekly');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [searchQuery, setSearchQuery] = useState(''); // New Search State

    // Stats States
    const [stats, setStats] = useState({ credit: 0, debit: 0, total: 0 });

    useEffect(() => {
        const init = async () => {
            if (!auth.currentUser?.uid) return;
            try {
                setLoading(true);
                const userDoc = await getDoc(doc(db, "users", auth.currentUser?.uid));
                const sId = userDoc.data()?.schoolId;
                setSchoolId(sId);

                if (sId) {
                    await fetchAllTransactions(sId);
                }
            } catch (e) {
                console.error("Initialization Error: ", e);
            } finally {
                setLoading(false);
            }
        };
        init();
    }, []);

    const fetchAllTransactions = async (sId) => {
        try {
            const q = query(
                collection(db, "wallet_transactions"),
                where("schoolId", "==", sId),
                orderBy("timestamp", "desc")
            );
            const snap = await getDocs(q);
            
            const txns = snap.docs.map(d => {
                const data = d.data();
                let parsedDate = new Date();
                if (data.timestamp) {
                    parsedDate = data.timestamp.toDate ? data.timestamp.toDate() : new Date(data.timestamp);
                } else if (data.dateTime) {
                    parsedDate = new Date(data.dateTime);
                }
                return { id: d.id, ...data, parsedDate };
            });

            setAllTransactions(txns);
            setDisplayedTxns(txns);
        } catch (e) {
            console.error("Fetch All Transactions Error: ", e);
        }
    };

    // Main Filtering Logic (Combines Date View + Search Query)
    useEffect(() => {
        let temp = [...allTransactions];
        const now = new Date();

        // 1. Apply Date Filters
        if (viewFilter === 'weekly') {
            const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
            startOfWeek.setHours(0, 0, 0, 0);
            temp = temp.filter(t => t.parsedDate >= startOfWeek);
        } else if (viewFilter === 'custom') {
            if (startDate) {
                const start = new Date(startDate);
                start.setHours(0, 0, 0, 0);
                temp = temp.filter(t => t.parsedDate >= start);
            }
            if (endDate) {
                const end = new Date(endDate);
                end.setHours(23, 59, 59, 999);
                temp = temp.filter(t => t.parsedDate <= end);
            }
        }

        // 2. Apply Text Search Filter
        if (searchQuery.trim() !== '') {
            const lowerQuery = searchQuery.toLowerCase();
            temp = temp.filter(t => 
                (t.studentName && t.studentName.toLowerCase().includes(lowerQuery)) ||
                (t.receiptNo && t.receiptNo.toLowerCase().includes(lowerQuery)) ||
                (t.bookReceiptNo && t.bookReceiptNo.toLowerCase().includes(lowerQuery)) ||
                (t.description && t.description.toLowerCase().includes(lowerQuery))
            );
        }

        // 3. Calculate Stats for the currently visible list
        let credit = 0, debit = 0;
        temp.forEach(t => {
            if (t.type === 'Credit') credit += Number(t.amount || 0);
            else debit += Number(t.amount || 0);
        });

        setStats({ credit, debit, total: credit - debit });
        setDisplayedTxns(temp);
    }, [viewFilter, startDate, endDate, searchQuery, allTransactions]);

    const handleFilterChange = (event, newValue) => {
        if (newValue !== null) {
            setViewFilter(newValue);
            if (newValue !== 'custom') {
                setStartDate('');
                setEndDate('');
            }
        }
    };

    // Date Formatter: DD/MM/YYYY
    const formatDate = (dateObj) => {
        if (!dateObj) return '';
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const year = dateObj.getFullYear();
        return `${day}/${month}/${year}`;
    };

    // Compact Stat Card Component
    const StatCard = ({ title, amount, icon, color, bgcolor }) => (
        <Paper elevation={0} sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ bgcolor, color, p: 0.5, borderRadius: 1, display: 'flex' }}>
                {icon}
            </Box>
            <Box>
                <Typography variant="caption" color="textSecondary" fontWeight="600" textTransform="uppercase">
                    {title}
                </Typography>
                <Typography variant="subtitle1" fontWeight="bold" color={SECONDARY} lineHeight={1}>
                    ₹{amount.toLocaleString('en-IN')}
                </Typography>
            </Box>
        </Paper>
    );

    return (
        <Box sx={{ bgcolor: "#f8fafc", minHeight: '100%', p: { xs: 1, sm: 2 } }}>
            {loading && <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, '& .MuiLinearProgress-bar': { bgcolor: PRIMARY } }} />}

            {/* Header & Controls */}
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2} mb={2}>
                <Typography variant="subtitle1" fontWeight="bold" color={SECONDARY}>
                    Wallet Overview
                </Typography>

                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ rowGap: 1 }}>
                    {/* Search Bar */}
                    <TextField
                        size="small"
                        placeholder="Search student, receipt..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ 
                            bgcolor: 'white', 
                            flexGrow: 1,
                            minWidth: { xs: '100%', sm: '200px' }, 
                            '& input': { p: 1, pl: 0, fontSize: '0.75rem' } 
                        }}
                    />

                    {/* Date Toggles */}
                    <ToggleButtonGroup
                        value={viewFilter}
                        exclusive
                        onChange={handleFilterChange}
                        size="small"
                        sx={{ bgcolor: 'white', height: 32 }}
                    >
                        <ToggleButton value="all" sx={{ px: 2, fontSize: '0.75rem', fontWeight: 600 }}>All</ToggleButton>
                        <ToggleButton value="weekly" sx={{ px: 2, fontSize: '0.75rem', fontWeight: 600 }}>Week</ToggleButton>
                        <ToggleButton value="custom" sx={{ px: 2, fontSize: '0.75rem', fontWeight: 600 }}>Custom</ToggleButton>
                    </ToggleButtonGroup>

                    {/* Custom Date Pickers */}
                    {viewFilter === 'custom' && (
                        <Stack direction="row" spacing={1}>
                            <TextField 
                                size="small" type="date" 
                                InputLabelProps={{ shrink: true }} 
                                value={startDate} onChange={(e) => setStartDate(e.target.value)} 
                                sx={{ width: 120, bgcolor: 'white', '& input': { p: 1, fontSize: '0.75rem' } }}
                            />
                            <TextField 
                                size="small" type="date" 
                                InputLabelProps={{ shrink: true }} 
                                value={endDate} onChange={(e) => setEndDate(e.target.value)} 
                                sx={{ width: 120, bgcolor: 'white', '& input': { p: 1, fontSize: '0.75rem' } }}
                            />
                        </Stack>
                    )}
                </Stack>
            </Stack>

            {/* Compact Stats */}
            <Grid container spacing={2} mb={2}>
                <Grid item xs={12} sm={4}>
                    <StatCard title="Inflow (Credit)" amount={stats.credit} icon={<TrendingUp fontSize="small" />} color="#16a34a" bgcolor="#dcfce7" />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <StatCard title="Outflow (Debit)" amount={stats.debit} icon={<TrendingDown fontSize="small" />} color="#dc2626" bgcolor="#ffe4e6" />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <StatCard title="Net Flow" amount={stats.total} icon={<AccountBalanceWallet fontSize="small" />} color={SECONDARY} bgcolor="#f1f5f9" />
                </Grid>
            </Grid>

            {/* Dense Data Table */}
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, maxHeight: 'calc(100vh - 200px)' }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600, bgcolor: '#f1f5f9', color: '#475569', fontSize: '0.75rem' }}>Date</TableCell>
                            <TableCell sx={{ fontWeight: 600, bgcolor: '#f1f5f9', color: '#475569', fontSize: '0.75rem' }}>Student</TableCell>
                            <TableCell sx={{ fontWeight: 600, bgcolor: '#f1f5f9', color: '#475569', fontSize: '0.75rem' }}>Receipt</TableCell>
                            <TableCell sx={{ fontWeight: 600, bgcolor: '#f1f5f9', color: '#475569', fontSize: '0.75rem' }}>Details</TableCell>
                            <TableCell sx={{ fontWeight: 600, bgcolor: '#f1f5f9', color: '#475569', fontSize: '0.75rem' }}>Mode</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, bgcolor: '#f1f5f9', color: '#475569', fontSize: '0.75rem' }}>Amount</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {displayedTxns.length > 0 ? displayedTxns.map((row) => (
                            <TableRow key={row.id} hover sx={{ '& td': { py: 1, borderBottom: '1px solid #f1f5f9' } }}>
                                <TableCell sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <EventNote sx={{ fontSize: 14 }} />
                                        <span>{formatDate(row.parsedDate)}</span>
                                    </Stack>
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, color: PRIMARY }}>
                                    {row.studentName || 'Unknown'}
                                </TableCell>
                                <TableCell>
                                    <Stack direction="row" spacing={0.5}>
                                        {row.receiptNo && <Chip size="small" label={row.receiptNo} sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, bgcolor: '#e0f2fe', color: '#0369a1' }} />}
                                        {row.bookReceiptNo && <Chip size="small" variant="outlined" label={`M: ${row.bookReceiptNo}`} sx={{ height: 20, fontSize: '0.65rem' }} />}
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        {row.type === 'Credit' 
                                            ? <TrendingUp sx={{ color: '#16a34a', fontSize: 16 }} /> 
                                            : <TrendingDown sx={{ color: '#dc2626', fontSize: 16 }} />
                                        }
                                        <Box>
                                            <Typography variant="body2" fontSize="0.75rem" fontWeight="600" lineHeight={1.2}>{row.description}</Typography>
                                        </Box>
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="caption" sx={{ px: 1, py: 0.2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 1, fontWeight: 500 }}>
                                        {row.payMode || 'Auto'}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8rem', color: row.type === 'Credit' ? '#059669' : '#e11d48' }}>
                                    {row.type === 'Credit' ? '+' : '-'} ₹{row.amount?.toLocaleString('en-IN')}
                                </TableCell>
                            </TableRow>
                        )) : (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                    <Stack alignItems="center" spacing={1} color="#94a3b8">
                                        <SearchOff fontSize="large" />
                                        <Typography variant="body2">No transactions match your search or dates.</Typography>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

export default Allwallet;